import { FADashboardType } from '@/constants/ReportDashboard/dashboards/dashboard-types'
import { Dashboard } from '@/services/report-dashboard/dashboard.service'

// The built-in dashboards that aren't part of the fetched dashboard list.
export const DEFAULT_DASHBOARD_KEY = 'Default Dashboard'
export const DEFAULT_ATTRIBUTION_DASHBOARD_KEY = 'Default Attribution Dashboard'

export const getDashboardKey = (dashboard: Pick<Dashboard, 'userId' | 'dashboardName'>): string =>
  `${dashboard.userId}-${dashboard.dashboardName}`

export const isValidDashboardKey = (
  key: string | null | undefined,
  dashboardList: readonly Dashboard[],
): key is string => !!key && dashboardList.some((d) => getDashboardKey(d) === key)

// Same as isValidDashboardKey, but also accepts the built-in default sentinels —
// use this when validating a key that may legitimately point at a default.
export const isSelectableDashboardKey = (
  key: string | null | undefined,
  dashboardList: readonly Dashboard[],
): key is string =>
  key === DEFAULT_DASHBOARD_KEY ||
  key === DEFAULT_ATTRIBUTION_DASHBOARD_KEY ||
  isValidDashboardKey(key, dashboardList)

export const getDashboardDisplayName = (
  dashboardKey: string,
  dashboardList: readonly Dashboard[],
): string => {
  if (dashboardKey === DEFAULT_DASHBOARD_KEY) return 'Risk Dashboard Default'
  if (dashboardKey === DEFAULT_ATTRIBUTION_DASHBOARD_KEY) return 'Attribution Dashboard Default'
  const found = dashboardList.find((d) => getDashboardKey(d) === dashboardKey)
  return found?.dashboardDisplayName ?? found?.dashboardName ?? 'Unknown Dashboard'
}

// Untyped/legacy/not-yet-loaded dashboards default to Risk.
export const getDashboardType = (
  dashboardKey: string,
  dashboardList: readonly Dashboard[],
): FADashboardType => {
  if (dashboardKey === DEFAULT_DASHBOARD_KEY) return FADashboardType.Risk
  if (dashboardKey === DEFAULT_ATTRIBUTION_DASHBOARD_KEY) return FADashboardType.Attribution
  const found = dashboardList.find((d) => getDashboardKey(d) === dashboardKey)
  return found?.dashboardType ?? FADashboardType.Risk
}

// The built-in defaults aren't saved dashboards the user can own — treat them as not owned so
// callers fall back to the same "save a copy to edit" flow as any other dashboard they don't own.
export const isDashboardOwnedByCurrentUser = (
  dashboardKey: string,
  dashboardList: readonly Dashboard[],
): boolean => {
  if (dashboardKey === DEFAULT_DASHBOARD_KEY || dashboardKey === DEFAULT_ATTRIBUTION_DASHBOARD_KEY) return false
  const found = dashboardList.find((d) => getDashboardKey(d) === dashboardKey)
  return found?.currentUserOwned ?? false
}
